import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/axios';
import Breadcrumb from '../../components/ui/Breadcrumb';
import toast from 'react-hot-toast';

const statusLabels = {
  draft:    { label: 'Borrador',  color: 'badge-secondary' },
  sent:     { label: 'Enviado',   color: 'badge-info' },
  accepted: { label: 'Aceptado', color: 'badge-success' },
};

const AccountsReceivable = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('all'); // all | overdue

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/accounts-receivable/summary');
      setData(res.data);
    } catch (e) {
      toast.error('Error al cargar cuentas por cobrar');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (amount, currency = 'mxn', rate = 1) => {
    if (currency === 'usd') {
      return `$${(amount / (rate || 1)).toLocaleString('en-US', { minimumFractionDigits: 2 })} USD`;
    }
    return `$${Number(amount).toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`;
  };

  const fmtMXN = (amount) =>
    `$${Number(amount).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

  const items = data?.items ?? [];
  const visible = filter === 'overdue' ? items.filter(i => i.is_overdue) : items;
  const s = data?.summary ?? {};

  return (
    <div className="container-fluid">
      <Breadcrumb items={[
        { label: 'Presupuestos', to: '/budgets' },
        { label: 'Cuentas por Cobrar' },
      ]} />

      <div className="page-header d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Cuentas por Cobrar</h4>
        <button className="btn btn-outline-secondary btn-sm" onClick={fetchData}>
          <i className="ti-reload mr-1"></i> Actualizar
        </button>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary"></div>
        </div>
      ) : (
        <>
          {/* ── Tarjetas de resumen ── */}
          <div className="row mb-4">
            <div className="col-12 col-md-4 mb-3">
              <div className="card h-100 border-left-warning" style={{ borderLeft: '4px solid #f39c12' }}>
                <div className="card-body">
                  <div className="text-muted small mb-1">Saldo Total Pendiente</div>
                  <h4 className="mb-0 text-warning">{fmtMXN(s.total_pending)}</h4>
                  <small className="text-muted">{s.count_pending} presupuesto{s.count_pending !== 1 ? 's' : ''} con saldo</small>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-4 mb-3">
              <div className="card h-100 border-left-danger" style={{ borderLeft: '4px solid #e74c3c' }}>
                <div className="card-body">
                  <div className="text-muted small mb-1">Saldo Vencido (+30 días)</div>
                  <h4 className="mb-0 text-danger">{fmtMXN(s.total_overdue)}</h4>
                  <small className="text-muted">{s.count_overdue} presupuesto{s.count_overdue !== 1 ? 's' : ''} vencido{s.count_overdue !== 1 ? 's' : ''}</small>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-4 mb-3">
              <div className="card h-100 border-left-success" style={{ borderLeft: '4px solid #27ae60' }}>
                <div className="card-body">
                  <div className="text-muted small mb-1">Total Cobrado (validado)</div>
                  <h4 className="mb-0 text-success">{fmtMXN(s.total_paid)}</h4>
                  <small className="text-muted">Pagos validados por el notario</small>
                </div>
              </div>
            </div>
          </div>

          {/* ── Filtros rápidos ── */}
          <div className="mb-3 d-flex" style={{ gap: '8px' }}>
            <button
              className={`btn btn-sm ${filter === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setFilter('all')}>
              Todos ({items.length})
            </button>
            <button
              className={`btn btn-sm ${filter === 'overdue' ? 'btn-danger' : 'btn-outline-danger'}`}
              onClick={() => setFilter('overdue')}>
              <i className="ti-alert mr-1"></i>
              Vencidos ({s.count_overdue})
            </button>
          </div>

          {/* ── Tabla ── */}
          <div className="card">
            <div className="card-body p-0">
              {visible.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <i className="ti-check-box" style={{ fontSize: '3rem', color: '#27ae60' }}></i>
                  <p className="mt-2 mb-0">
                    {filter === 'overdue'
                      ? 'No hay presupuestos vencidos'
                      : 'No hay saldos pendientes'}
                  </p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0" style={{ tableLayout: 'fixed' }}>
                    <thead>
                      <tr>
                        <th style={{ width: '90px' }}>Folio</th>
                        <th>Cliente / Expediente</th>
                        <th className="mobile-hide" style={{ width: '140px' }}>Tipo documento</th>
                        <th className="mobile-hide" style={{ width: '110px' }}>Responsable</th>
                        <th style={{ width: '110px' }}>Total</th>
                        <th style={{ width: '100px' }}>Pagado</th>
                        <th style={{ width: '110px' }}>Saldo</th>
                        <th className="mobile-hide" style={{ width: '80px' }}>Días</th>
                        <th style={{ width: '80px' }}>Estado</th>
                        <th style={{ width: '50px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map(item => (
                        <tr key={item.id}
                          style={{ background: item.is_overdue ? '#fff8f8' : undefined }}>
                          <td>
                            <span className="font-weight-bold small">{item.folio}</span>
                          </td>
                          <td>
                            <div className="font-weight-bold small"
                              style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.client_name}
                            </div>
                            {item.case_file && (
                              <small className="text-muted"
                                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                                {item.case_file.title}
                              </small>
                            )}
                          </td>
                          <td className="mobile-hide">
                            <small className="text-muted"
                              style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                              {item.document_type}
                            </small>
                          </td>
                          <td className="mobile-hide">
                            <small>{item.responsible}</small>
                          </td>
                          <td>
                            <small className="font-weight-bold">
                              {fmt(item.grand_total, item.currency, item.exchange_rate)}
                            </small>
                          </td>
                          <td>
                            {item.total_paid > 0 ? (
                              <span className="badge badge-success" style={{ fontSize: '11px' }}>
                                {fmtMXN(item.total_paid)}
                              </span>
                            ) : (
                              <span className="text-muted small">—</span>
                            )}
                          </td>
                          <td>
                            <span
                              className="font-weight-bold small"
                              style={{ color: item.is_overdue ? '#e74c3c' : '#e67e22' }}>
                              {fmtMXN(item.balance)}
                              {item.is_overdue && (
                                <i className="ti-alert ml-1" title="Vencido" style={{ fontSize: '11px' }}></i>
                              )}
                            </span>
                          </td>
                          <td className="mobile-hide">
                            <span
                              className={`badge ${item.days_open > 30 ? 'badge-danger' : item.days_open > 15 ? 'badge-warning' : 'badge-secondary'}`}
                              style={{ fontSize: '11px' }}>
                              {item.days_open}d
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${statusLabels[item.status]?.color ?? 'badge-secondary'}`}
                              style={{ fontSize: '11px' }}>
                              {statusLabels[item.status]?.label ?? item.status}
                            </span>
                          </td>
                          <td>
                            <Link to={`/budgets/${item.id}`}
                              className="btn btn-sm btn-outline-primary"
                              title="Ver presupuesto">
                              <i className="ti-eye"></i>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AccountsReceivable;